import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AddPetsModal,
  PetSelection,
} from 'us-react-components/dist/AddPetsModal/AddPetsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons/faClipboardList';

import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { getShortDateString } from 'us-react-components/dist/utils/date';
import { JobService, Job, ParentService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const SITTER_IMAGE_BASE = 'https://assets.urbansitter.com/static/img/sitters/sitter-';

// Date-only range, no time component — "Jan 1, 2024 - Jan 5, 2024".
const formatBookingDateRange = (start?: string, end?: string): string => {
  const startStr = getShortDateString(start);
  if (!startStr) return '';
  const endStr = getShortDateString(end);
  return endStr ? `${startStr} - ${endStr}` : startStr;
};

// Port of apps/angular/src/js/ng/parent/job/job-success-to-search-service.js
const getJobSearchParams = (job: Job): Record<string, string> => {
  const params: Record<string, string> = {};
  const booking = job.booking;
  if (booking?.address?.city) params.city = booking.address.city;
  if (booking?.address?.state) params.state = booking.address.state;
  if (booking?.numberOfChildren != null) {
    params.numberOfChildren = String(booking.numberOfChildren);
  }
  if (booking?.typeId != null) {
    params.verticalTypeId = String(booking.typeId);
  }
  if (booking?.paymentType === 'credit') {
    params.acceptsCreditCard = 'true';
  }
  if (job.type === 'recurring') {
    params.searchTypeCode = job.availability === 'Full Time' ? 'FULL_TIME' : 'PART_TIME';
    params.showSaveModal = 'true';
  } else {
    if (booking?.start) params.start = booking.start;
    if (booking?.end) params.end = booking.end;
  }
  return params;
};

export const JobSuccess = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent;

  const [job, setJob] = useState<Job | null>(null);
  const [addPetsOpen, setAddPetsOpen] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Job Posted';
  }, []);

  useEffect(() => {
    if (!user?.parent) {
      window.location.href = '/';
      return;
    }
    const jobId = Number(pathId);
    if (!jobId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await JobService.findById(jobId);
        setJob(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[JobSuccess] load failed', err);
      }
    };
    void load();
  }, [pathId, user?.parent]);

  const randomSitterNums = useMemo(() => {
    const picked: number[] = [];
    while (picked.length < 8) {
      const n = Math.floor(Math.random() * 18) + 1;
      if (!picked.includes(n)) picked.push(n);
    }
    return picked;
  }, []);

  const oneTime = job?.type === 'one_time';
  const recurring = job?.type === 'recurring';
  const headingText = oneTime
    ? t('pages.jobSuccess.oneTime')
    : recurring
      ? (job?.availability ?? '')
      : '';
  const headingTime = oneTime
    ? formatBookingDateRange(job?.booking?.start, job?.booking?.end)
    : recurring
      ? t('pages.jobSuccess.startingPrefix', { date: getShortDateString(job?.booking?.start) })
      : '';
  const subText = oneTime
    ? t('pages.jobSuccess.oneTimeSub')
    : recurring
      ? t('pages.jobSuccess.recurringSub', {
          availability: (job?.availability ?? '').toLowerCase(),
        })
      : '';

  const typeId = (job?.booking as { typeId?: number } | undefined)?.typeId;
  const isPetCare = typeId === bookingVerticalType.PET_CARE;
  const isHouseholdServices = typeId === bookingVerticalType.HOUSEHOLD_SERVICES;
  const isSeniorCare = typeId === bookingVerticalType.SENIOR_CARE;

  const hasPets = (): boolean => !!(parent.dog || parent.cat || parent.otherPet);

  const navigateToSearch = (params: Record<string, string>): void => {
    const qs = new URLSearchParams(params);
    navigate(`/sitters?${qs.toString()}`);
  };

  const goToSearch = (): void => {
    if (!job) return;
    const searchParams = getJobSearchParams(job);
    if (isPetCare) {
      if (hasPets()) {
        if (parent.dog) searchParams.willSitDog = 'true';
        if (parent.cat) searchParams.willSitCat = 'true';
        if (parent.otherPet) searchParams.willSitOtherPet = 'true';
        navigateToSearch(searchParams);
      } else {
        setAddPetsOpen(true);
      }
    } else {
      if (isHouseholdServices) searchParams.willSitHome = 'true';
      if (isSeniorCare) searchParams.willSitSenior = 'true';
      navigateToSearch(searchParams);
    }
    tracker.track('job-success', {
      action: 'post-job-sounds-good',
      elementName: 'Sounds Good',
    });
  };

  const goToJobList = (): void => {
    tracker.track('job-success', {
      action: 'post-job-no-thanks',
      elementName: 'No Thanks',
    });
    navigate('/posts');
  };

  const savePets = async (pets: PetSelection): Promise<void> => {
    if (!parent.id || !job) return;
    try {
      await ParentService.patch(parent.id, {
        id: parent.id,
        dog: pets.dog,
        cat: pets.cat,
        otherPet: pets.otherPet,
      } as Parameters<typeof ParentService.patch>[1]);
      await stores.parent.refreshParent();
    } catch (err) {
      console.warn('[JobSuccess] update pets failed', err);
    }
    const searchParams = getJobSearchParams(job);
    if (pets.dog) searchParams.willSitDog = 'true';
    if (pets.cat) searchParams.willSitCat = 'true';
    if (pets.otherPet) searchParams.willSitOtherPet = 'true';
    setAddPetsOpen(false);
    navigateToSearch(searchParams);
  };

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-2'>{t('pages.jobSuccess.heading')}</h1>
      <h4 className='text-base mb-4'>{t('pages.jobSuccess.subheading')}</h4>
      <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
        <div className='md:col-span-9'>
          <div className='us-card p-4 rounded'>
            <div className='flex items-end gap-3'>
              <FontAwesomeIcon icon={faClipboardList} className='text-3xl text-sapphire' aria-hidden='true' />
              <h4 className='inline-block text-lg'>
                {headingText}
                <br />
                <span>{headingTime}</span>
              </h4>
            </div>
            <hr className='my-3' />
            <div className='mb-4'>
              <h4 className='font-semibold'>
                {t('pages.jobSuccess.whileWaitingPrefix')} {subText}
              </h4>
              {t('pages.jobSuccess.autoClose')}
            </div>
            <div
              className='grid grid-cols-4 sm:grid-cols-8 gap-2 cursor-pointer mb-4'
              onClick={goToSearch}
              onKeyDown={(e): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToSearch();
                }
              }}
              role='button'
              tabIndex={0}
            >
              {randomSitterNums.map((n) => (
                <img
                  key={n}
                  src={`${SITTER_IMAGE_BASE}${n}.webp`}
                  alt=''
                  className='w-full aspect-square rounded object-cover'
                />
              ))}
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-12 gap-3'>
              <div className='sm:col-span-4'>
                <Button
                  id='post-success-to-search'
                  buttonType={ButtonType.primary}
                  onClick={goToSearch}
                  className='w-full'
                >
                  {t('pages.jobSuccess.soundsGood')}
                </Button>
              </div>
              <div className='sm:col-span-4'>
                <Button
                  id='post-success-to-list'
                  buttonType={ButtonType.secondary}
                  onClick={goToJobList}
                  className='w-full'
                >
                  {t('pages.jobSuccess.noThanks')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddPetsModal
        isOpen={addPetsOpen}
        onClose={(): void => setAddPetsOpen(false)}
        onSave={savePets}
      />
    </div>
  );
});
